/// A câmera do aparelho, com as bordas do navegador já traduzidas.
///
/// Mora no design system, e não no módulo de investimento, porque fotografar um papel é gesto de
/// sistema: hoje é comprovante, amanhã é contrato ou nota. O que é do comprovante — o que aceita,
/// quanto pesa — continua com quem chama.

export type CameraFacing = 'environment' | 'user';

/// Nem todo lugar tem câmera para oferecer. Em conexão insegura — `http` fora do `localhost` — o
/// navegador **não expõe** `mediaDevices`, e o tipo do DOM não conta isso: ele jura que sempre
/// existe. Perguntar antes evita pintar um botão que só sabe dar erro.
export function cameraAvailable(): boolean {
  if (typeof navigator === 'undefined') return false;
  return typeof navigator.mediaDevices?.getUserMedia === 'function';
}

export type CameraStream = { ok: true; stream: MediaStream } | { ok: false; detail: string };

export async function openCamera(facing: CameraFacing): Promise<CameraStream> {
  if (!cameraAvailable()) {
    return {
      ok: false,
      detail: 'Este navegador não libera a câmera fora de uma conexão segura.',
    };
  }
  try {
    /// `ideal`, e não `exact`: o notebook tem uma câmera só, e exigir a traseira faria o pedido
    /// falhar justamente onde ele deveria usar a que existe.
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: facing } },
    });
    return { ok: true, stream };
  } catch (error) {
    return { ok: false, detail: describeCameraError(error) };
  }
}

/// Cada recusa do navegador pede uma saída diferente de quem está lendo: liberar a permissão,
/// desistir, ou fechar o aplicativo que está com a câmera. Um "não foi possível" para os três casos
/// deixaria a pessoa sem o que fazer.
function describeCameraError(error: unknown): string {
  const name = error instanceof Error ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Permissão de câmera negada. Libere o acesso nas configurações do navegador e tente de novo.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'Nenhuma câmera foi encontrada neste aparelho.';
  }
  if (name === 'NotReadableError') {
    return 'A câmera está sendo usada por outro aplicativo.';
  }
  return 'Não foi possível abrir a câmera.';
}

/// Desligar de verdade, faixa por faixa. Soltar a referência não apaga a luz ao lado da lente — e
/// uma câmera que continua ligada depois de a janela fechar é o tipo de coisa que se descobre pelo
/// aviso do sistema operacional.
export function stopCamera(stream: MediaStream | null): void {
  for (const track of stream?.getTracks() ?? []) track.stop();
}

/// Quantas câmeras o aparelho tem. Sem permissão o navegador esconde o **nome** de cada uma, mas
/// não esconde quantas são — e é só disso que depende oferecer, ou não, o botão de virar a câmera.
export async function countCameras(): Promise<number> {
  try {
    const devices = (await navigator.mediaDevices?.enumerateDevices()) ?? [];
    return devices.filter((device) => device.kind === 'videoinput').length;
  } catch {
    return 0;
  }
}

/// A foto é o quadro que está na tela, no tamanho nativo do sensor — e não no tamanho em que a
/// prévia coube. Fotografar um papel e receber a imagem do tamanho da caixinha de prévia daria um
/// comprovante ilegível.
///
/// Devolve `null` quando não há quadro ainda, quando o navegador não dá contexto de desenho ou
/// quando a codificação falha. São três motivos e uma saída só: tentar de novo.
export async function photoFromVideo(
  video: HTMLVideoElement,
  fileName: string,
): Promise<File | null> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (width === 0 || height === 0) return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.drawImage(video, 0, 0, width, height);

  /// JPEG, e não PNG: papel fotografado é foto, e o PNG do mesmo quadro sai várias vezes maior sem
  /// ficar mais legível — e o limite de 10 MB do comprovante é real.
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((result) => resolve(result), 'image/jpeg', 0.9);
  });
  if (!blob) return null;
  return new File([blob], fileName, { type: 'image/jpeg' });
}
